import { ChipTypes } from "helpers/getDefectChipTypesCc";
import i18n from "i18n";

// Builds a [{ label, value }] list summarising the user's currently applied
// filters. Source of truth is FilterContext.filterValues — each entry has a
// .value (the selected option) and .params (the URL/API representation).
//
// We deliberately avoid hardcoding which filter keys exist; instead we walk
// what's present and rely on a small label + reader registry for the keys we
// know about. Unknown filter keys are skipped rather than displayed raw, since
// raw param shapes wouldn't be useful to a user.

const showAll = () => i18n.t("label.showAll");

// Chips render on the page as `${ChipTypes(chipType)[0].caption} : ${value}`
// (see EMIChip). Match that shape here so the modal's "current page filters"
// list reads identically to what the user sees in the chip row above.
const chipLabel = (chip) =>
	ChipTypes(chip?.chipType)?.[0]?.caption || i18n.t("label.filter");

const chipDisplayValue = (chip) => {
	if (!chip) return "";
	// chipType === 8 is the boolean / yes-no chip variant; everything else uses value.
	if (chip.chipType === 8) return chip.value ? i18n.t("label.yes") : i18n.t("label.no");
	return chip.value ?? chip.name ?? "";
};

const valueName = (value) => {
	if (!value) return null;
	if (Array.isArray(value)) {
		const names = value.map((v) => v?.name).filter(Boolean);
		return names.length ? names.join(", ") : null;
	}
	return value?.name || null;
};

// Timeframe option names carry the current ISO week number in brackets, e.g.
// "Last Week (20)" (see filterByDateOptions in TimeframeFilter). That number is
// only meaningful at selection time — a subscription persists this summary and
// re-runs weeks later, so the bracketed week would be stale/misleading on the
// card. Strip a trailing " (NN)" so only the relative label is kept. Custom
// ranges ("Customized Date", "12/05/2026 - 18/05/2026") have no such suffix
// and are left untouched.
const stripWeekNumber = (name) =>
	typeof name === "string" ? name.replace(/\s*\(\d+\)\s*$/, "") : name;

const filterReaders = {
	defectStatus: { labelKey: "label.status", read: (entry) => valueName(entry.value) || showAll() },
	serviceStatus: { labelKey: "label.status", read: (entry) => valueName(entry.value) || showAll() },
	timeframe: { labelKey: "label.timeframe", read: (entry) => stripWeekNumber(valueName(entry.value)) || showAll() },
	department: { labelKey: "entity.department", read: (entry) => valueName(entry.value) || showAll() },
	model: { labelKey: "label.model", read: (entry) => valueName(entry.value) || showAll() },
	role: { labelKey: "entity.role", read: (entry) => valueName(entry.value) || showAll() },
	asset: { labelKey: "entity.asset", read: (entry) => valueName(entry.value) || showAll() },
	interval: { labelKey: "entity.interval", read: (entry) => valueName(entry.value) || showAll() },
	riskRatings: { labelKey: "entity.riskRating", read: (entry) => valueName(entry.value) || showAll() },
	defectType: { labelKey: "label.type", read: (entry) => valueName(entry.value) || showAll() },
};

// Chips need bespoke handling because each chip carries its own label/value
// pair (the registry above maps one filter key -> one summary row, but a
// chip array produces N rows, one per chip).
const readChips = (entry) => {
	const chips = entry?.value;
	if (!Array.isArray(chips) || chips.length === 0) return [];
	return chips
		.map((chip) => ({
			label: chipLabel(chip),
			value: chipDisplayValue(chip),
		}))
		.filter((row) => row.value);
};

export const formatFilterSummary = (filterValues = {}) => {
	// Chips first — they're typically the most specific selection the user
	// has made (a particular Model, Asset, Risk Rating) so they should rank
	// above broader dropdowns like Timeframe / Status when downstream
	// consumers (e.g. suggestSubscriptionName) take the leading entries.
	const summary = readChips(filterValues.chips);
	for (const [key, entry] of Object.entries(filterValues)) {
		if (key === "chips") continue;
		const reader = filterReaders[key];
		if (!reader || !entry) continue;
		const value = reader.read(entry);
		if (!value) continue;
		summary.push({ label: i18n.t(reader.labelKey), value });
	}
	return summary;
};

export const formatFilterSummaryInline = (filterValues = {}) =>
	formatFilterSummary(filterValues)
		.map(({ label, value }) => `${label} = ${value}`)
		.join(", ");
